import { ButtonV2, Checkbox, ModalV2, Spinner, TooltipV2 } from '@cimplico/components';
import { loadStripe, StripePaymentElement } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { usePaymentMethodSetupIntent } from '../../../../queries/billing/use-pm-setup-intent';
import { CreditCardIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { useState } from 'react';
import queryClient from '../../../../query-client';
import { useSetDefaultPaymentMethod } from '../../../../queries/billing/use-set-default-payment-method';
import { toastNotification } from '../../../../components/toast/Toast';
import { useVerifyPaymentMethod } from '../../../../queries/billing/use-verify-payment-method';
import axios from 'axios';

const stripePublishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
if (!stripePublishableKey) {
    throw new Error('Stripe publishable key not found');
}

const stripePromise = loadStripe(stripePublishableKey);

type NewCardModalProps = {
    firstPaymentMethod: boolean;
    onClose: () => void;
};

function NewCardModal({ firstPaymentMethod, onClose }: NewCardModalProps): JSX.Element {
    const query = usePaymentMethodSetupIntent();
    const secret = query.data?.intent;
    const [isFormSubmitting, setIsFormSubmitting] = useState(false);

    return (
        <ModalV2
            isOpen
            onClose={(): void => {
                // Prevent onClose from firing while the payment forming is submitting
                if (!isFormSubmitting) {
                    onClose();
                }
            }}
        >
            <ModalV2.Header>
                <div className="flex items-center gap-2">
                    <CreditCardIcon className="text-contentSecondary h-5 w-5" />
                    <p className="font-bold text-contentPrimary">Add a new card</p>
                    <button
                        className="ml-auto"
                        onClick={(): void => {
                            // Prevent onClose from firing while the payment forming is submitting
                            if (!isFormSubmitting) {
                                onClose();
                            }
                        }}
                    >
                        <XMarkIcon className="text-contentSecondary h-5 w-5" />
                    </button>
                </div>
            </ModalV2.Header>
            {query.isLoading && (
                <div className="h-[300px] w-full flex flex-col items-center justify-center">
                    <Spinner />
                    <p className="text-sm text-contentSecondary mt-1">Loading payment form...</p>
                </div>
            )}
            {secret && (
                <Elements stripe={stripePromise} options={{ clientSecret: secret }}>
                    <StripeCardForm
                        firstPaymentMethod={firstPaymentMethod}
                        onClose={onClose}
                        onSubmitting={(): void => setIsFormSubmitting(true)}
                        onFinishSubmitting={(): void => setIsFormSubmitting(false)}
                    />
                </Elements>
            )}
        </ModalV2>
    );
}

type StripeCardFormProps = {
    firstPaymentMethod: boolean;
    onClose: () => void;
    // Callback fired when the form starts submitting
    onSubmitting: () => void;
    // Callback fired when the form is done submitting
    onFinishSubmitting: () => void;
};

function StripeCardForm({ firstPaymentMethod, onClose, onSubmitting, onFinishSubmitting }: StripeCardFormProps): JSX.Element {
    const stripe = useStripe();
    const elements = useElements();
    const setDefaultQuery = useSetDefaultPaymentMethod();
    const verifyQuery = useVerifyPaymentMethod();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [formLoaded, setFormLoaded] = useState(false);
    const [isDefault, setIsDefault] = useState(true);

    /**
     * Triggers Stripe elements to save the input payment method against the customer.
     * Duplicate card checks and setting as default will also be handled after submission.
     * @param e Form event
     * @returns
     */
    async function handleSubmit(e?: React.FormEvent<HTMLFormElement>): Promise<void> {
        e && e.preventDefault();
        if (!stripe || !elements) return;
        setLoading(true);
        onSubmitting();
        setError(null);

        const { setupIntent, error } = await stripe.confirmSetup({
            elements,
            confirmParams: {
                return_url: window.location.href, // optional redirect if needed
            },
            redirect: 'if_required',
        });

        // Something went wrong on Stripe's end, just show an error and let the user close the form
        if (error) {
            setError(error.message ?? 'An error occurred');
            setLoading(false);
            onFinishSubmitting();
        } else {
            // For some reason Stripe was successful, but we can't access the payment method id.
            if (!setupIntent.payment_method) {
                setError('Unable to add payment method');
                setLoading(false);
                onFinishSubmitting();
                return;
            }

            // Check if the new payment method is a duplicate. (This can only be done on the backend)
            try {
                await verifyQuery.mutateAsync({ paymentMethodId: setupIntent.payment_method.toString() });
                // Invalidate billing query
                queryClient.invalidateQueries(['upcoming-invoice']);
                queryClient.invalidateQueries(['payment-methods']);
            } catch (err) {
                if (axios.isAxiosError(err)) {
                    setError(err.response?.data?.message ?? 'Unable to add payment method - An unknown error occurred');
                } else {
                    setError('Unable to add payment method - An unknown error occurred');
                }
                setLoading(false);
                onFinishSubmitting();
                return;
            }
            setLoading(false);
            // If the user has elected this payment method to be default, send a request to the backend to update in Stripe.
            if (isDefault && setupIntent.payment_method) {
                try {
                    await setDefaultQuery.mutateAsync({ paymentMethodId: setupIntent.payment_method.toString() });
                    toastNotification('success', 'Successfully added a default payment method');
                    onClose();
                } catch (error) {
                    setError('Unable to set this payment method as default');
                } finally {
                    queryClient.invalidateQueries('payment-methods');
                    onFinishSubmitting();
                }
            } else {
                queryClient.invalidateQueries('payment-methods');
                toastNotification('success', 'Successfully added a payment method');
                onClose();
            }
        }
    }

    return (
        <>
            <ModalV2.Body>
                {error && (
                    <div className="bg-red-50 border-y border-red-300 h-12 -ml-4 -mr-4 -mt-4 mb-4 flex items-center pl-4">
                        <ExclamationCircleIcon className="h-5 w-5 text-red-800 mr-2" />
                        <p className="text-red-800 font-medium text-sm text-left">{error}</p>
                    </div>
                )}

                <form className="pl-1.5" id="payment-form" onSubmit={handleSubmit}>
                    <div className="w-[400px] flex flex-col gap-4">
                        <PaymentElement
                            onReady={(el: StripePaymentElement): void => {
                                el && el.focus();
                                setFormLoaded(true);
                            }}
                        />
                        {formLoaded && (
                            <TooltipV2
                                disabled={!firstPaymentMethod}
                                message={
                                    'The first card you save will be the default payment method. You can update this when additional cards are added.'
                                }
                                position={'left'}
                            >
                                <Checkbox
                                    label="Set as default payment method"
                                    checked={firstPaymentMethod || isDefault}
                                    onChange={(e): void => setIsDefault(e.target.checked)}
                                    disabled={firstPaymentMethod}
                                />
                            </TooltipV2>
                        )}
                    </div>
                </form>
            </ModalV2.Body>
            <ModalV2.Footer>
                <div className="flex items-center gap-2 justify-end">
                    <ButtonV2 label="Cancel" type="secondary" size="sm" onClick={onClose} isDisabled={loading || !formLoaded} />
                    <ButtonV2
                        label={loading ? 'Saving...' : 'Add Payment Method'}
                        type="primary"
                        size="sm"
                        isLoading={loading}
                        isDisabled={loading || !formLoaded}
                        onClick={handleSubmit}
                    />
                </div>
            </ModalV2.Footer>
        </>
    );
}

export default NewCardModal;
